import { useMutation } from '@tanstack/react-query';
import { DELETE_ERROR_MESSAGE, DELETE_SUCCESS_MESSAGE } from '@/constants/messages';
import useAppToast from '@/hooks/useAppToast';
import { deleteUpload } from '@/services/upload/uploadService';
import type { UploadPortal } from '@/types/upload';
import { getApiErrorMessage } from '@/utils/apiError';
import { useUploadPortal } from './useUploadPortal';

export interface UseUploadDeleteOptions {
  portal?: UploadPortal;
  siteType?: string;
  clinicNumber?: string;
  /** Disable success toast — useful when callers show their own feedback. */
  silentSuccess?: boolean;
}

/**
 * Delete an uploaded file + its token.
 * Backend refuses deletion if the token is already attached to an entity.
 */
export const useUploadDelete = (options: UseUploadDeleteOptions = {}) => {
  const { portal, params } = useUploadPortal({
    portal: options.portal,
    siteType: options.siteType,
    clinicNumber: options.clinicNumber,
  });
  const toast = useAppToast();

  const mutation = useMutation<void, Error, string>({
    mutationFn: token => deleteUpload(portal, params, token),
    onSuccess: () => {
      if (!options.silentSuccess) {
        toast.success(DELETE_SUCCESS_MESSAGE);
      }
    },
    onError: error => {
      toast.error(getApiErrorMessage(error, DELETE_ERROR_MESSAGE));
    },
    meta: { suppressNetworkToast: true },
  });

  return {
    remove: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
  };
};
